// KPI Builder — moving between the questions and the JSON editor.
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import { builderAnswersState, builderModeState, Visualization } from 'states';
import { importAdvanced } from 'helpers/kpiBuilder/advanced';
import type { FormBridge } from 'helpers/kpiBuilder/formBridge';
import { BuilderAnswers, EMPTY_ANSWERS } from 'helpers/kpiBuilder/types';

/** The stored config holds the dashboard filters too; the form keeps them in a field of their own. */
function withFilters(config: string, kpiFilters: string): string {
  try {
    const list = JSON.parse(kpiFilters || '[]');
    return Array.isArray(list) && list.length ? JSON.stringify({ ...JSON.parse(config || '{}'), kpiFilters: list }) : config;
  } catch {
    return config;
  }
}

/** A hand-written KPI's queries and config as parts, or null when something in it cannot be shown as parts. */
function readHandWritten(form: FormBridge, saved: Visualization | undefined): BuilderAnswers | null {
  const imported = importAdvanced(form.get('queries'), withFilters(form.get('config'), form.get('kpiFilters')), saved?.data?.[0]?.info?.handlerKey);
  return imported ? { ...EMPTY_ANSWERS, recipe: 'advanced', ...imported, chart: form.get('chartType') || null } : null;
}

export function useModeSwitches(form: FormBridge, answers: BuilderAnswers, saved: Visualization | undefined, setSwitchedToJson: (v: boolean) => void) {
  const setAnswers = useSetRecoilState(builderAnswersState);
  const setMode = useSetRecoilState(builderModeState);

  /** Super admins only: continue by hand from what the builder generated. */
  const switchToJson = useCallback(() => {
    setSwitchedToJson(true);
    setMode(false);
  }, [setMode, setSwitchedToJson]);

  /**
   * Go back to the questions. A hand-written KPI has no answers, so its queries and config are read into parts instead.
   * Returns false when something in it cannot be shown as parts, and the JSON editor stays.
   */
  const switchToBuilder = useCallback((): boolean => {
    const readIn = answers.recipe ? null : readHandWritten(form, saved);
    if (!answers.recipe && !readIn) return false;
    if (readIn) setAnswers(readIn);
    setSwitchedToJson(false);
    setMode(true);
    return true;
    // form and saved are read when called; the callback only needs refreshing when the recipe changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.recipe, saved, setMode]);

  return { switchToJson, switchToBuilder };
}
